import { ObjectConfig, required } from "@homebound/form-state";
import { ChangeEventReadyPlanOptionInput, SaveChangeEventInput } from "src/generated/graphqlTypes";
import {
  baseChangeEventFormConfig,
  BaseChangeEventFormInput,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { GuidedChangeEventPage_ChangeEventFragment } from "../../GuidedChangeEventPage.generated";

export type ApplyConfigurationFormInput = BaseChangeEventFormInput &
  Pick<
    SaveChangeEventInput,
    "readyPlanId" | "originalReadyPlanId" | "originalReadyPlanOptions" | "proposedReadyPlanOptions" | "checkoutConfigId"
  >;

const readyPlanOptionConfig: ObjectConfig<ChangeEventReadyPlanOptionInput> = {
  id: { type: "value" },
  readyPlanOptionId: { type: "value", rules: [required] },
  priceInCents: { type: "value" },
};

// Exported so the partial workflow and the tests build their form state from the same config.
export const applyConfigurationFormConfig: ObjectConfig<ApplyConfigurationFormInput> = {
  ...baseChangeEventFormConfig,
  readyPlanId: { type: "value", rules: [required] },
  // A structure with no current configuration legitimately has neither of these
  originalReadyPlanId: { type: "value" },
  originalReadyPlanOptions: { type: "list", config: readyPlanOptionConfig },
  checkoutConfigId: { type: "value", rules: [required] },
  proposedReadyPlanOptions: {
    type: "list",
    // The options are what actually gets applied, so an empty list means there is nothing to save
    rules: [({ value }) => (value.nonEmpty ? undefined : "At least one configuration option is required")],
    config: readyPlanOptionConfig,
  },
};

/**
 * Rebuilds the form from a saved change event, so edit mode / a mid-wizard refresh comes back to
 * what was already saved.
 */
export function applyConfigurationMapToForm(
  changeEvent: GuidedChangeEventPage_ChangeEventFragment,
): ApplyConfigurationFormInput {
  return {
    id: changeEvent.id,
    workflow: changeEvent.workflow?.code,
    projectStageId: changeEvent.projectStage.id,
    type: changeEvent.type.code,
    title: changeEvent.title,
    internalNote: changeEvent.internalNote,
    reasonId: changeEvent.reason.id,
    hasPreSignedContract: changeEvent.hasPreSignedContract,
    documentId: changeEvent.document?.id,
    documentName: changeEvent.document?.name,
    checkoutConfigId: changeEvent.checkoutConfig?.id,
    readyPlanId: changeEvent.readyPlan?.id,
    originalReadyPlanId: changeEvent.originalReadyPlan?.id,
    originalReadyPlanOptions: changeEvent.originalReadyPlanOptions.map(toOptionInput),
    proposedReadyPlanOptions: changeEvent.proposedReadyPlanOptions.map(toOptionInput),
  };
}

function toOptionInput(
  option: Pick<
    GuidedChangeEventPage_ChangeEventFragment["originalReadyPlanOptions"][number],
    "id" | "priceInCents" | "readyPlanOption"
  >,
): ChangeEventReadyPlanOptionInput {
  return { id: option.id, readyPlanOptionId: option.readyPlanOption.id, priceInCents: option.priceInCents };
}
